import { useTokens } from './theme';

/**
 * Styled on/off switch (DOM port of the RN <Switch> used in UserDetail's
 * feature-flag row). role="switch" + aria-checked; brand focus ring; the hit
 * area is padded to a 44px min touch target while the visual track stays 24px.
 *
 * Name it with a visible `label` (rendered beside the track, inside the button)
 * or, when the text sits elsewhere, `aria-label`. The off track carries a 1px
 * controlBorder ring so its boundary clears 3:1 on the portal.
 *
 * @param {{
 *   checked?: boolean,
 *   onChange?: (next: boolean) => void,
 *   disabled?: boolean,
 *   label?: React.ReactNode,
 *   'aria-label'?: string,
 * }} props
 */
export function Switch({ checked, onChange, disabled, label, 'aria-label': ariaLabel }) {
  const t = useTokens();
  return (
    <button
      type="button"
      role="switch"
      aria-checked={!!checked}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={() => { if (!disabled) { onChange?.(!checked); } }}
      className={`inline-flex items-center gap-2 min-h-[44px] focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 rounded-full ${t.focusRing} ${disabled ? 'opacity-55 cursor-not-allowed' : ''}`}
    >
      <span
        className="relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors"
        style={{
          backgroundColor: checked ? t.switchOn : t.switchOff,
          boxShadow: checked ? 'none' : `inset 0 0 0 1px ${t.controlBorder}`,
        }}
      >
        <span
          className={`inline-block h-5 w-5 rounded-full transition-transform ${checked ? 'translate-x-5' : 'translate-x-0.5'}`}
          style={{ backgroundColor: t.switchKnob }}
        />
      </span>
      {label ? (
        <span className="text-sm" style={{ color: t.body, fontFamily: t.fonts.body }}>{label}</span>
      ) : null}
    </button>
  );
}

export default Switch;
